import {Dispatch, SetStateAction} from 'react'
import {
    AtaDetalhe,
    AtaResumo,
    DocumentoProcessoOut,
    EsferaCodigo,
    PesquisaVinculadaOut,
    RespostasEntrevista,
} from '../types'
import {brl, ESFERA_LABEL, fmtDate, itemAderenteLocal} from '../ui'
import ComparacaoAtas from './ComparacaoAtas'

/** Rótulo amigável do tipo detectado pelo backend (heurística sobre o texto). */
const TIPO_DOC_LABEL: Record<string, string> = {
    ETP: 'Estudo Técnico Preliminar',
    DFD: 'Documento de Formalização da Demanda',
    TR: 'Termo de Referência',
    OUTRO: 'Documento de planejamento',
}

const ESFERAS_SUBNACIONAIS = ['E', 'M', 'D']

interface Props {
    ata: AtaDetalhe
    termoBuscado: string
    aderenteNome: string
    respostas: RespostasEntrevista
    setRespostas: Dispatch<SetStateAction<RespostasEntrevista>>
    gerando: boolean
    onGerar: () => void
    onVoltar: () => void
    /** Handoff ao módulo de Pesquisa de Preços (IN 65) — opcional (testes). */
    onPesquisarPrecos?: () => void
    /** Pesquisa já vinculada à justificativa desta ata (status/valores). */
    pesquisaVinculada?: PesquisaVinculadaOut | null
    /** Outras atas do mesmo objeto que a busca trouxe (comparação opcional). */
    alternativas?: AtaResumo[]
    onAdotarAta?: (numeroControle: string) => void
    onAbrirDetalhe?: (numeroControle: string) => void
    // Documento de planejamento (DFD/ETP/TR) — o contexto principal da redação.
    documento?: DocumentoProcessoOut | null
    documentosDisponiveis?: DocumentoProcessoOut[]
    enviandoDocumento?: boolean
    erroDocumento?: string | null
    onEnviarDocumento?: (file: File) => void
    onSelecionarDocumento?: (id: string) => void
    onRemoverDocumento?: () => void
}

/** Fluxo AUTOMÁTICO da Ata Inteligente: encontrou a ata → (opcional) anexa um DFD/
 * documento técnico como contexto → gera. Sem entrevista: as verificações legais
 * rodam sozinhas e tudo o que depender de confirmação do órgão vira LEMBRETE na
 * versão final (a minuta nunca trava por pendência). Quantidades, comparação de
 * atas e pesquisa de preços são opcionais. */
export default function GerarJustificativa(
    {ata, termoBuscado, aderenteNome, respostas, setRespostas, gerando, onGerar, onVoltar,
     onPesquisarPrecos, pesquisaVinculada = null, alternativas = [], onAdotarAta, onAbrirDetalhe,
     documento = null, documentosDisponiveis = [], enviandoDocumento = false, erroDocumento = null,
     onEnviarDocumento, onSelecionarDocumento, onRemoverDocumento}: Props
) {
    const e = respostas
    const set = (patch: Partial<RespostasEntrevista>) => setRespostas((prev) => ({...prev, ...patch}))
    // Rascunho legado pode ter persistido quantidades_desejadas: null (o spread da
    // reidratação não repõe o default) — normaliza aqui e no merge funcional.
    const qtds = e.quantidades_desejadas || {}
    const setQtd = (item: number, v: number) =>
        setRespostas((prev) => ({
            ...prev,
            quantidades_desejadas: {...(prev.quantidades_desejadas || {}), [String(item)]: v},
        }))

    const vedado = e.esfera_aderente === 'F' && ESFERAS_SUBNACIONAIS.includes(ata.esfera_codigo)
    const vigente = ['VIGENTE', 'A_VENCER'].includes(ata.status_vigencia)
    const qtdInformada = Object.values(qtds).some((v) => (v || 0) > 0)
    // Seleção de itens feita no DETALHE da ata: o painel NÃO reapresenta a lista
    // (pedido do dono — quem acabou de escolher não precisa rever); o bloco de
    // itens só aparece em fluxos SEM seleção (rascunhos/registros legados).
    const selecaoFeita = (e.itens_selecionados || []).length > 0

    // Com a Pesquisa de Preços vinculada rodando DE FATO (status conhecido e não
    // terminal), a geração espera — o orçamento estimado é insumo da fundamentação
    // e entra sozinho quando a pesquisa conclui. ERRO → libera (não tranca o
    // gestor por falha alheia); status null ("vinculada — aguardando dados", a
    // leitura best-effort falhou) também NÃO bloqueia: nada garante que há algo
    // rodando, e travar aqui seria um beco sem saída.
    const aguardandoPesquisa = !!pesquisaVinculada
        && !pesquisaVinculada.concluida
        && !!pesquisaVinculada.status
        && pesquisaVinculada.status !== 'ERRO'

    /** Gerar com pendência DURA (vedação de esfera / ata vencida) exige confirmação
     * explícita — não trava (a minuta sai marcada), mas o clique não pode ser
     * inocente: gasta 1 crédito num documento que a lei provavelmente barra. */
    function confirmarEGerar() {
        // Pesquisa VINCULADA com dados ilegíveis agora (status null): não trava
        // (beco sem saída), mas o clique confirma — se ela estiver rodando, a v2
        // incorpora os valores automaticamente ao concluir.
        if (pesquisaVinculada && !pesquisaVinculada.concluida && !pesquisaVinculada.status) {
            if (!window.confirm(
                'Há uma pesquisa de preços vinculada cujos dados não puderam ser lidos agora. '
                + 'Gerar mesmo assim? Se ela estiver em andamento, a justificativa será atualizada (v2) automaticamente quando concluir.'
            )) {
                return
            }
        }
        if (vedado || !vigente) {
            const motivos = [
                ...(vedado ? ['a adesão por órgão federal a ata estadual/municipal/distrital é vedada (art. 86, §8º)'] : []),
                ...(!vigente ? ['a ata está fora de vigência (art. 84)'] : []),
            ].join(' e ')
            if (!window.confirm(`Atenção: ${motivos}. Gerar mesmo assim (1 documento)? A minuta sairá com a pendência marcada.`)) {
                return
            }
        }
        onGerar()
    }

    return (
        <div>
            <header className="page-header">
                <div className="crumb">
                    <a onClick={onVoltar}>Detalhe da ata</a>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <span>Gerar justificativa</span>
                </div>
                <div className="header-row"><h1 className="lg-h2">Gerar justificativa para o ETP</h1></div>
                <p className="lg-text-muted" style={{marginBottom: 0}}>
                    Ata {ata.numero_ata || '—'} · {ata.orgao} · vigente até {fmtDate(ata.vigencia_fim)} · órgão aderente <strong>{aderenteNome}</strong>
                </p>
                <p className="lg-text-muted" style={{margin: '4px 0 0'}}>
                    Objeto: {ata.objeto}
                </p>
            </header>

            <div className="page-content" style={{maxWidth: 760}}>
                <div className="callout callout--info" style={{marginBottom: 22}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/></svg>
                    <div>
                        Anexe um documento técnico do processo (DFD, ETP ou TR) e clique em gerar — a Licito.Guru IA
                        redige a justificativa a partir dele. O que depender de confirmação do seu órgão aparece como
                        <strong> lembrete na versão final</strong>.
                    </div>
                </div>

                {/* ---- Verificações automáticas (nada a preencher) ---- */}
                <div style={{display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22}}>
                    <div className={`callout ${vigente ? 'callout--success' : 'callout--danger'}`} style={{margin: 0}}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{__html: vigente ? '<path d="M20 6 9 17l-5-5"/>' : '<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>'}} />
                        <div>{vigente ? `Ata vigente até ${fmtDate(ata.vigencia_fim)}.` : 'Ata fora de vigência — a pendência constará da minuta.'}</div>
                    </div>
                    <div className={`callout ${vedado ? 'callout--danger' : 'callout--success'}`} style={{margin: 0}}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{__html: vedado ? '<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>' : '<path d="M20 6 9 17l-5-5"/>'}} />
                        <div style={{flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8}}>
                            <span>
                                {vedado
                                    ? `Vedação do art. 86, §8º: órgão federal não adere a ata ${(ESFERA_LABEL[ata.esfera_codigo] || '').toLowerCase()}.`
                                    : `Esferas compatíveis: aderente ${ESFERA_LABEL[e.esfera_aderente]} × gerenciador ${ESFERA_LABEL[ata.esfera_codigo] || ata.esfera}.`}
                            </span>
                            <label style={{display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)'}}>
                                Esfera do aderente:
                                <select
                                    className="lg-select" style={{width: 'auto', padding: '2px 26px 2px 8px'}}
                                    value={e.esfera_aderente}
                                    aria-label="Esfera do órgão aderente"
                                    onChange={(ev) => set({esfera_aderente: ev.target.value as EsferaCodigo})}
                                >
                                    <option value="F">Federal</option>
                                    <option value="E">Estadual</option>
                                    <option value="M">Municipal</option>
                                    <option value="D">Distrital</option>
                                </select>
                            </label>
                        </div>
                    </div>
                </div>

                {/* ---- Documento de contexto (o único insumo pedido) ---- */}
                {onEnviarDocumento && (
                    <div className="bloco">
                        <div className="bloco__head">
                            <div>
                                <div className="bloco__title">Documento técnico (recomendado)</div>
                                <div className="bloco__req">DFD, ETP, TR ou outro documento do processo — a Licito.Guru IA redige a justificativa a partir dele</div>
                            </div>
                        </div>
                        <div className="bloco__body">
                            {enviandoDocumento ? (
                                <div className="callout callout--info" style={{margin: 0}}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                                    <div>Extraindo o texto do documento…</div>
                                </div>
                            ) : documento ? (
                                <div className="callout callout--success" style={{margin: 0, alignItems: 'center'}}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                                    <div style={{flex: 1}}>
                                        <strong>{documento.nome_arquivo}</strong>
                                        <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)'}}>
                                            Lido como {TIPO_DOC_LABEL[documento.tipo_sugerido] || TIPO_DOC_LABEL.OUTRO}. Será usado na redação da justificativa.
                                        </div>
                                    </div>
                                    {onRemoverDocumento && (
                                        <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onRemoverDocumento}>Remover</button>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <label className="lg-btn lg-btn--secondary lg-btn--sm" style={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6}}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                        Escolher arquivo (PDF ou DOCX, até 10MB)
                                        <input
                                            type="file" accept=".pdf,.docx" style={{display: 'none'}}
                                            onChange={(ev) => {
                                                const f = ev.target.files && ev.target.files[0]
                                                if (f) onEnviarDocumento(f)
                                                ev.target.value = '' // permite reenviar o mesmo arquivo após erro
                                            }}
                                        />
                                    </label>
                                    {documentosDisponiveis.length > 0 && onSelecionarDocumento && (
                                        <select
                                            className="lg-select" style={{marginTop: 8}} value=""
                                            onChange={(ev) => { if (ev.target.value) onSelecionarDocumento(ev.target.value) }}
                                        >
                                            <option value="">Ou selecione um documento já enviado…</option>
                                            {documentosDisponiveis.map((d) => (
                                                <option key={d.id} value={d.id}>{d.nome_arquivo} ({TIPO_DOC_LABEL[d.tipo_sugerido] || 'Documento'})</option>
                                            ))}
                                        </select>
                                    )}
                                    <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', margin: '8px 0 0'}}>
                                        Sem documento a geração também funciona — a redação parte do item pesquisado e dos dados da ata.
                                    </p>
                                </>
                            )}
                            {erroDocumento && !enviandoDocumento && (
                                <div className="callout callout--danger" style={{margin: '8px 0 0'}}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                    <div>{erroDocumento}</div>
                                </div>
                            )}
                            <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', margin: '8px 0 0'}}>
                                O conteúdo do documento é lido pela Licito.Guru IA e usado apenas como contexto para redigir a
                                justificativa — nada é copiado automaticamente nem anexado ao documento final.
                            </p>
                        </div>
                    </div>
                )}

                {/* ---- Opcionais: quantidades, comparação, pesquisa de preços ---- */}
                {!selecaoFeita && <div className="bloco" style={{marginTop: 14}}>
                    <div className="bloco__head">
                        <div>
                            <div className="bloco__title">Itens registrados na ata</div>
                            <div className="bloco__req">
                                {qtdInformada
                                    ? 'quantidade pretendida informada: o teto de 50% por item (art. 86, §3º) será verificado'
                                    : 'se já souber, informe a quantidade pretendida (opcional); sem ela, o teto de 50% fica como lembrete'}
                            </div>
                        </div>
                    </div>
                    <div className="bloco__body">
                        {ata.itens.length === 0 && <p style={{fontSize: 'var(--fs-sm)', color: 'var(--text-tertiary)', margin: 0}}>Esta ata não tem itens cadastrados.</p>}
                        {ata.itens.map((it) => {
                            // Teto autoritativo do backend (art. 86, §3º) — nunca recalcular no cliente.
                            const limite = it.limite_50pct
                            const desejado = qtds[String(it.numero_item)] || 0
                            const excede = desejado > limite
                            // Item RECOMENDADO para a busca do gestor: sinal do backend
                            // (?termo= no detalhe) com fallback local (matcher normalizado).
                            // Apoio VISUAL apenas — nada é bloqueado; pode haver vários.
                            const recomendado = Boolean(it.aderente) || itemAderenteLocal(termoBuscado, it.descricao)
                            return (
                                <div
                                    key={it.numero_item}
                                    style={{
                                        display: 'grid', gridTemplateColumns: '1fr 130px', gap: 12, alignItems: 'center',
                                        padding: '10px 0', borderBottom: '1px solid var(--border-subtle)',
                                        ...(recomendado ? {
                                            background: 'var(--feedback-info-soft)',
                                            borderLeft: '3px solid var(--feedback-info)',
                                            paddingLeft: 10, paddingRight: 6,
                                        } : {}),
                                    }}
                                >
                                    <div>
                                        <div style={{fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' as any, color: 'var(--text-primary)'}}>
                                            Item {it.numero_item} — {it.descricao}
                                            {recomendado && (
                                                <span
                                                    className="lg-badge lg-badge--info"
                                                    title={`Recomendado para a busca "${(termoBuscado || '').trim()}"`}
                                                    style={{marginLeft: 8, verticalAlign: 'middle'}}
                                                >
                                                    Recomendado para a sua busca
                                                </span>
                                            )}
                                        </div>
                                        <div style={{fontSize: 'var(--fs-xs)', color: excede ? 'var(--feedback-danger)' : 'var(--text-tertiary)'}}>
                                            Registrado: {it.qtd_registrada.toLocaleString('pt-BR')} · limite 50%: {limite.toLocaleString('pt-BR')}{it.valor_unitario != null ? <> · <strong style={{color: 'var(--text-secondary)'}}>{brl(it.valor_unitario)}{(it.unidade || '').trim() ? `/${it.unidade}` : ''}</strong></> : null} {excede ? '· ⚠ excede o teto individual' : ''}
                                        </div>
                                    </div>
                                    <input
                                        className="lg-input" type="number" min={0} value={desejado}
                                        aria-label={`Quantidade pretendida do item ${it.numero_item}`}
                                        onChange={(ev) => setQtd(it.numero_item, parseInt(ev.target.value || '0', 10))}
                                        style={excede ? {borderColor: 'var(--feedback-danger)'} : undefined}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div>}

                <details className="bloco" style={{marginTop: 14}}>
                    <summary style={{cursor: 'pointer', padding: '12px 16px', fontWeight: 'var(--fw-medium)' as any, color: 'var(--text-primary)'}}>
                        Comparar com atas semelhantes (opcional)
                        <span style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', marginLeft: 8}}>
                            Acórdão 547/2026-TCU — o quadro comparativo entra sozinho na minuta
                        </span>
                    </summary>
                    <div className="bloco__body">
                        <ComparacaoAtas
                            ata={ata} termoBuscado={termoBuscado} alternativas={alternativas}
                            onAdotarAta={onAdotarAta} onAbrirDetalhe={onAbrirDetalhe}
                        />
                    </div>
                </details>

                {onPesquisarPrecos && (
                    <div className="bloco" style={{marginTop: 14}}>
                        <div className="bloco__body" style={{paddingTop: 14}}>
                            <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10}}>
                                <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onPesquisarPrecos}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                                    Pesquisar preços (IN 65) — opcional
                                </button>
                                <span style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)'}}>
                                    Comprova a vantajosidade com pesquisa própria: o resultado volta vinculado, a geração espera a conclusão e os valores entram na fundamentação. Sem ela, a comprovação fica como lembrete.
                                </span>
                            </div>
                            {pesquisaVinculada && (
                                <div className={`callout ${pesquisaVinculada.concluida ? 'callout--success' : 'callout--info'}`} style={{margin: '10px 0 0'}}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
                                    <div>
                                        {pesquisaVinculada.concluida ? (
                                            <>Pesquisa de preços vinculada e <strong>concluída</strong>
                                            {pesquisaVinculada.valores?.mediana != null && <> — mediana {brl(pesquisaVinculada.valores.mediana)}</>}
                                            {pesquisaVinculada.valores
                                                ? '. Os valores de referência entram como evidência da vantajosidade.'
                                                : ' — sem valores de referência apurados.'}</>
                                        ) : pesquisaVinculada.status ? (
                                            <>Pesquisa de preços vinculada, <strong>em andamento</strong> — a geração libera automaticamente quando ela concluir.</>
                                        ) : (
                                            <>Pesquisa de preços <strong>vinculada</strong> — aguardando dados.</>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ---- CTA ---- */}
                <div style={{marginTop: 22}}>
                    <button className="lg-btn lg-btn--brand lg-btn--block" disabled={gerando || aguardandoPesquisa} onClick={confirmarEGerar}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        {aguardandoPesquisa ? 'Aguardando a Pesquisa de Preços…' : gerando ? 'Gerando…' : 'Gerar justificativa (1 documento)'}
                    </button>
                    <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 8}}>
                        {aguardandoPesquisa
                            ? 'A Pesquisa de Preços vinculada está em andamento — a geração libera automaticamente quando ela concluir, já com o orçamento estimado na fundamentação.'
                            : 'A minuta é gerada mesmo com pontos em aberto — cada um aparece como lembrete na versão final, para você confirmar antes de formalizar.'}
                    </p>
                </div>
            </div>
        </div>
    )
}
